"use client";
import React from "react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/custom/ui/accordion";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/custom/ui/avatar";
import { Badge } from "@/components/ui/custom/ui/badge";
import { Button } from "@/components/ui/custom/ui/button";
import Icon from "@/components/ui/Icon";
import {
  Wallet,
  Coins,
  Gem,
  BarChart3,
  Award,
  LogIn,
  MonitorPlay,
  Users,
  Package,
  Network,
  Layers,
  ZapIcon,
} from "lucide-react";
import { truncateText } from "@/utils/generalutils";
import { supabase } from "@/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/custom/ui/select";

const statGroups = [
  {
    title: "Points",
    items: [
      {
        key: "mxs",
        label: "MXS",
        icon: Coins,
        color: "text-yellow-400",
        image:
          "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/branding%2Fmxs_games%2Fv1%2Fwhite%2Fpng%2Fmxs_icon_white.png?alt=media&token=8a7e36ba-954b-46b3-bcb2-559cec885bdf",
      },
      {
        key: "gold",
        label: "Gold",
        icon: Coins,
        color: "text-yellow-500",
        image:
          "https://res.cloudinary.com/dot2onqdb/image/upload/v1729068489/gold_lr_h4yg2m.png",
      },
      {
        key: "ruby",
        label: "Ruby",
        icon: Gem,
        color: "text-red-500",
        image: "/assets/points/ruby.png",
      },
      {
        key: "sapphire",
        label: "Sapphire",
        icon: Gem,
        color: "text-blue-500",
        image: "/assets/points/sapphire.png",
      },
      {
        key: "xb",
        label: "XB",
        icon: Coins,
        color: "text-green-400",
        image:
          "https://res.cloudinary.com/dot2onqdb/image/upload/v1729067770/xseed_lr_is7mcx.png",
      },
    ],
  },
  {
    title: "Activity",
    items: [
      {
        key: "level_count",
        label: "Levels",
        icon: BarChart3,
        color: "text-purple-400",
      },
      {
        key: "missions_count",
        label: "Missions",
        icon: Award,
        color: "text-orange-400",
      },
      {
        key: "login_count",
        label: "Logins",
        icon: LogIn,
        color: "text-blue-400",
      },
      {
        key: "ads_count",
        label: "Ads Viewed",
        icon: MonitorPlay,
        color: "text-gray-400",
      },
    ],
  },
  {
    title: "Referrals",
    items: [
      {
        key: "user_referral_count",
        label: "Users",
        icon: Users,
        color: "text-green-400",
      },
      {
        key: "products_referral",
        label: "Products",
        icon: Package,
        color: "text-yellow-400",
      },
      {
        key: "node_referral_count",
        label: "Nodes",
        icon: Network,
        color: "text-blue-400",
      },
    ],
  },
  {
    title: "NFTs",
    items: [
      {
        key: "nft_count",
        label: "Count",
        icon: Layers,
        color: "text-purple-400",
      },
      {
        key: "nft_power",
        label: "Power",
        icon: ZapIcon,
        color: "text-yellow-400",
      },
    ],
  },
];

const ProfileAccordion = ({
  user,
  walletAddress,
  isEligible,
  claimButtonLoading,
  status,
  currentUserResult,
  handleClaim,
  seasonData,
  seasonsTab,
  selectedSeason,
  setSelectedSeason,
}) => {
  const currentSeason = seasonsTab.find((s) => s.name === selectedSeason);
  const [daysLeft, setDaysLeft] = React.useState(null);
  const [seasonEnd, setSeasonEnd] = React.useState(null);
  const [userRank, setUserRank] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const isCalculationsUnderway =
    daysLeft === 0 && seasonData && !seasonData.is_reveal_criteria;

  React.useEffect(() => {
    if (currentSeason && currentSeason.end_date) {
      const end = new Date(currentSeason.end_date);
      setSeasonEnd(end.toISOString().slice(0, 10));
      const now = new Date();
      const diff = Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
      setDaysLeft(diff);
    }
    setLoading(false);
  }, [currentSeason, walletAddress]);

  // Fetch user rank for the selected season using mxs_rank from seasons_diff
  React.useEffect(() => {
    const fetchUserRank = async () => {
      setUserRank(null);
      if (!currentSeason?.id || !walletAddress) return;

      const tableName = currentSeason.is_active
        ? "seasons_diff"
        : `seasons_final_diff_snapshot_${currentSeason.id}`;

      try {
        const { data, error } = await supabase
          .from(tableName)
          .select("mxs_rank")
          .eq("wallet_lc", walletAddress.toLowerCase())
          .single();

        if (error) {
          setUserRank(null);
          return;
        }

        if (data && data.mxs_rank != null) {
          setUserRank(data.mxs_rank);
        } else {
          setUserRank(null);
        }
      } catch (e) {
        setUserRank(null);
      }
    };
    fetchUserRank();
  }, [currentSeason, walletAddress]);

  // Function to determine season status based on dates
  const getSeasonStatus = (startDate, endDate, isActive) => {
    if (isActive) return "Ongoing";

    const now = new Date();

    // Parse dates and handle potential null/undefined values
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (!start || !end) {
      return "Unknown";
    }

    const nowTime = now.getTime();
    const startTime = start.getTime();
    const endTime = end.getTime();

    if (nowTime >= startTime && nowTime <= endTime) {
      return "Ongoing";
    } else if (nowTime > endTime) {
      return "Completed";
    } else {
      return "Upcoming";
    }
  };

  // Compute availableSeasons from seasonsTab
  const now = new Date();
  // console.log("ProfileAccordion - seasonsTab:", seasonsTab);
  // console.log("ProfileAccordion - selectedSeason:", selectedSeason);
  const availableSeasons = seasonsTab
    .map((season) => {
      const status = getSeasonStatus(
        season.start_date,
        season.end_date,
        season.is_active
      );

      // console.log({
      //   season: season.name,
      //   startDate: season.start_date,
      //   endDate: season.end_date,
      //   now,
      //   status,
      // });

      return {
        number: season.number || season.id,
        name: season.name,
        status,
        endDate: season.end_date,
        isActive: status === "Ongoing",
        ...season,
      };
    })
    .sort((a, b) => (a.number || a.id) - (b.number || b.id));

  // console.log("ProfileAccordion - availableSeasons:", availableSeasons);

  // On mount or when seasonsTab changes, set selected season to ongoing, else most recent completed, else first
  React.useEffect(() => {
    if (!availableSeasons.length) return;
    let defaultSeason = availableSeasons.find((s) => s.is_active);
    if (!defaultSeason) {
      defaultSeason = availableSeasons.find((s) => s.status === "Ongoing");
    }
    if (!defaultSeason) {
      const completed = availableSeasons.filter(
        (s) => s.status === "Completed"
      );
      if (completed.length) {
        defaultSeason = completed[completed.length - 1];
      } else {
        defaultSeason = availableSeasons[0];
      }
    }
    if (defaultSeason && defaultSeason.name !== selectedSeason) {
      setSelectedSeason(defaultSeason.name);
    }
  }, [JSON.stringify(availableSeasons)]);

  // Function to get status icon and color
  const getStatusIcon = (status) => {
    switch (status) {
      case "Ongoing":
        return <Icon icon="ph:play-light" className="h-4 w-4 text-green-400" />;
      case "Completed":
        return (
          <Icon icon="mdi:check-circle" className="h-4 w-4 text-gray-400" />
        );
      case "Upcoming":
        return (
          <Icon icon="mdi:clock-outline" className="h-4 w-4 text-blue-400" />
        );
      default:
        return null;
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case "Ongoing":
        return "text-green-400";
      case "Completed":
        return "text-gray-400";
      case "Upcoming":
        return "text-blue-400";
      default:
        return "text-gray-400";
    }
  };

  // Mobile-friendly and cleaned up layout
  return (
    <div className="w-full mb-6">
      <div className="rounded-xl px-3 py-4 sm:px-8 sm:py-6 flex flex-col md:flex-row items-center md:items-center justify-between bg-gradient-to-r from-metaxseed-blue to-[#8f3fff] relative gap-y-4 md:gap-0">
        {/* Left: Avatar and Info (flex-1 on md+) */}
        <div className="w-full md:flex-1 flex flex-col md:flex-row md:items-center md:gap-6">
          {/* Avatar */}
          <div className="flex justify-center md:justify-start md:items-center">
            <div
              className={`rounded-full inline-flex ${
                !user
                  ? "bg-black"
                  : "bg-gradient-to-r from-[#fe5000] to-[#ffa500]"
              } p-[2px]`}
            >
              <Avatar className="h-16 w-16 sm:h-20 sm:w-20">
                {user ? (
                  <AvatarImage
                    src={
                      user.photo_url ||
                      "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fgeneral_media%2Fimages%2Fbranding2Fv12Fpng%252Fmxs_icon_white.webp?alt=media&token=d2f79745-29ca-4340-8d93-cd019c6885db"
                    }
                    className="object-cover"
                  />
                ) : (
                  <AvatarImage
                    src="https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fgeneral_media%2Fimages%2Fbranding2Fv12Fpng%252Fmxs_icon_white.webp?alt=media&token=d2f79745-29ca-4340-8d93-cd019c6885db"
                    className="object-cover"
                  />
                )}
              </Avatar>
            </div>
          </div>
          {/* User Info and Dropdown/Rank */}
          <div className="flex flex-col items-center md:items-start w-full md:w-auto md:ml-6 mt-2 md:mt-0">
            <div className="text-white text-center md:text-start text-xl sm:text-2xl font-bold leading-tight mb-1">
              {user
                ? `Welcome, ${user.name}!`
                : "Welcome to MetaXSeed Seasons!"}
            </div>
            {user && (
              <div className="text-white/80 text-sm sm:text-base font-mono text-center md:text-start mb-1 flex items-center gap-2 justify-center md:justify-start">
                <Wallet className="h-4 w-4" />
                {truncateText(walletAddress, 6, 6)}
              </div>
            )}
            {/* Dropdown, Status, Rank row (row on md+, stacked on mobile) */}
            <div className="flex flex-wrap flex-col sm:flex-row md:flex-row items-center md:items-center gap-2 text-white/80 text-xs sm:text-sm mt-1 md:mt-2 w-full md:w-auto">
              <Select
                value={selectedSeason}
                onValueChange={(value) => setSelectedSeason(value)}
              >
                <SelectTrigger className="w-auto h-7 bg-white/20 border-white/30 text-white text-xs sm:text-sm [&>span]:text-white [&>svg]:text-white">
                  <SelectValue>
                    {(() => {
                      const currentSeason = availableSeasons.find(
                        (s) => s.name === selectedSeason
                      );
                      if (currentSeason) {
                        return (
                          <div className="flex items-center gap-2">
                            <span>Season {currentSeason.number}</span>
                            <span className="text-gray-200">•</span>
                            <div className="flex items-center gap-1.5">
                              {getStatusIcon(currentSeason.status)}
                              <span
                                className={`text-xs ${getStatusTextColor(
                                  currentSeason.status
                                )}`}
                              >
                                {currentSeason.status
                                  ? currentSeason.status
                                      .charAt(0)
                                      .toUpperCase() +
                                    currentSeason.status.slice(1)
                                  : "Unknown"}
                              </span>
                            </div>
                          </div>
                        );
                      }
                      return selectedSeason || "Select Season";
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {availableSeasons.map((season) => (
                    <SelectItem
                      key={season.number}
                      value={season.name}
                      className="text-gray-300 focus:bg-gray-700 focus:text-white"
                    >
                      <div className="flex items-center gap-3">
                        <span>Season {season.number}</span>
                        <span className="text-gray-200">•</span>
                        <div className="flex items-center gap-1.5">
                          {getStatusIcon(season.status)}
                          <span
                            className={`text-xs ${getStatusTextColor(
                              season.status
                            )}`}
                          >
                            {season.status
                              ? season.status.charAt(0).toUpperCase() +
                                season.status.slice(1)
                              : "Unknown"}
                          </span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {user && userRank != null && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span className="font-medium">Rank #{userRank}</span>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Right: Days Left and End/Claim Button (w-auto on md+) */}
        <div className="w-full md:w-auto flex flex-col items-end justify-end mt-4 md:mt-0 ml-0 md:ml-auto md:items-center md:justify-center">
          {isCalculationsUnderway ? (
            <div className="text-white/80 text-base sm:text-lg font-semibold text-right w-full md:w-auto">
              Calculations underway
            </div>
          ) : (
            <>
              <div className="text-4xl sm:text-5xl font-extrabold text-white leading-none text-right w-full md:w-auto">
                {daysLeft !== null ? daysLeft : "-"}
              </div>
              <div className="text-white/80 text-base sm:text-lg font-semibold text-right w-full md:w-auto">
                Days Left
              </div>
              <div className="flex flex-wrap items-center gap-2 text-white/70 text-xs sm:text-sm mt-1 justify-end w-full md:justify-center md:w-auto">
                {user && seasonData && seasonData.is_reveal_criteria ? (
                  <p>Claiming in progress</p>
                ) : (
                  <>
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm10 4V7a1 1 0 1 1-2 0V6H9v1a1 1 0 1 1-2 0V6H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-1Z"
                      />
                    </svg>
                    {(() => {
                      const now = new Date();
                      const end = seasonEnd ? new Date(seasonEnd) : null;
                      if (
                        end &&
                        now > end &&
                        seasonData?.status !== "distributed"
                      ) {
                        return (
                          <span>
                            Criteria will be revealed after rewards calculations
                            are completed
                          </span>
                        );
                      } else {
                        return <span>Ends {seasonEnd || "-"}</span>;
                      }
                    })()}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileAccordion;
